(() => {
  'use strict';

  /*
   * FREE GIFT SETTINGS
   * Replace the number below with your actual gift variant ID.
   */
  const GIFT_VARIANT_ID = 12345678901234;

  /*
   * Shopify money values use cents.
   * RM350.00 = 35000 cents.
   *
   * This code qualifies carts worth RM350 or more.
   * For strictly above RM350, change this to 35001.
   */
  const THRESHOLD_CENTS = 35000;

  /*
   * true: Open the cart drawer after the gift is added.
   * false: Add the gift without automatically opening the cart.
   */
  const OPEN_CART_AFTER_ADD = true;

  const ROOT_URL = window.Shopify?.routes?.root || '/';

  let isSyncing = false;
  let syncAgain = false;

  /**
   * Reads the current Shopify Ajax cart.
   */
  async function getCart() {
    const response = await fetch(`${ROOT_URL}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Unable to read cart: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Uses Shopify's standard cart action so the theme can refresh
   * its cart drawer, cart count and other cart interfaces.
   */
  async function updateCartLines(lines) {
    if (!window.Shopify?.actions?.updateCart) {
      throw new Error('Shopify cart actions are unavailable.');
    }

    const result = await window.Shopify.actions.updateCart(
      { lines },
      {
        event: {
          context: 'standard-action',
          detail: {
            source: 'automatic-free-gift',
          },
        },
      }
    );

    if (result.userErrors?.length) {
      throw new Error(result.userErrors[0].message);
    }

    if (result.warnings?.length) {
      console.warn('Free gift cart warning:', result.warnings);
    }

    return result;
  }

  /**
   * Opens Fabric's cart drawer when available.
   * Shopify falls back to the cart page when no drawer is detected.
   */
  async function openCart() {
    if (window.Shopify?.actions?.openCart) {
      await window.Shopify.actions.openCart();
    }
  }

  /**
   * Adds, removes or corrects the free gift.
   */
  async function syncFreeGift({ openAfterAdding = false } = {}) {
    if (isSyncing) {
      syncAgain = true;
      return;
    }

    isSyncing = true;

    try {
      const cart = await getCart();

      const giftLines = cart.items.filter(
        (item) => Number(item.variant_id) === GIFT_VARIANT_ID
      );

      /*
       * Calculate the qualifying subtotal while excluding the gift.
       * final_line_price is expressed in cents.
       */
      const qualifyingSubtotal = cart.items.reduce((subtotal, item) => {
        const isGift =
          Number(item.variant_id) === GIFT_VARIANT_ID;

        if (isGift) {
          return subtotal;
        }

        return subtotal + Number(item.final_line_price || 0);
      }, 0);

      const qualifiesForGift =
        qualifyingSubtotal >= THRESHOLD_CENTS;

      /*
       * Customer qualifies and no gift exists:
       * add one gift.
       */
      if (qualifiesForGift && giftLines.length === 0) {
        await updateCartLines([
          {
            merchandiseId: String(GIFT_VARIANT_ID),
            quantity: 1,
            attributes: [
              {
                key: '_free_gift',
                value: 'RM350 promotion',
              },
            ],
          },
        ]);

        if (OPEN_CART_AFTER_ADD && openAfterAdding) {
          await openCart();
        }

        return;
      }

      /*
       * Customer no longer qualifies:
       * remove every gift line.
       */
      if (!qualifiesForGift && giftLines.length > 0) {
        await updateCartLines(
          giftLines.map((line) => ({
            id: line.key,
            quantity: 0,
          }))
        );

        return;
      }

      /*
       * Customer qualifies, but the gift quantity has been changed
       * or multiple gift lines exist:
       * keep only one gift.
       */
      if (
        qualifiesForGift &&
        giftLines.length > 0 &&
        (
          giftLines.length > 1 ||
          giftLines[0].quantity !== 1
        )
      ) {
        const corrections = giftLines.map((line, index) => ({
          id: line.key,
          quantity: index === 0 ? 1 : 0,
        }));

        await updateCartLines(corrections);
      }
    } catch (error) {
      console.error('Automatic free gift error:', error);
    } finally {
      isSyncing = false;

      if (syncAgain) {
        syncAgain = false;
        window.setTimeout(() => {
          syncFreeGift();
        }, 100);
      }
    }
  }

  /*
   * Check the cart when the page first loads.
   * This does not automatically open the drawer.
   */
  document.addEventListener('DOMContentLoaded', () => {
    syncFreeGift();
  });

  /*
   * Recheck after customers add, remove or update cart items.
   * The event promise resolves after the cart operation finishes.
   */
  document.addEventListener(
    'shopify:cart:lines-update',
    (event) => {
      if (isSyncing) {
        return;
      }

      const cartOperation = event.promise
        ? Promise.resolve(event.promise)
        : Promise.resolve();

      cartOperation
        .catch(() => null)
        .finally(() => {
          syncFreeGift({
            openAfterAdding: true,
          });
        });
    }
  );

  /*
   * Recheck when returning through browser history.
   */
  window.addEventListener('pageshow', () => {
    syncFreeGift();
  });
})();